import { type Ref, ref } from 'vue';
import { EngagementMode } from '@/core/sourcing/outreach/outreach.persistence';

export interface UseResumeUploadReturn {
  selectedFiles: Ref<File[]>;
  selectedOption: Ref<EngagementMode>;
  addFiles: (files: File[]) => void;
  removeFile: (index: number) => void;
  clearAllFiles: () => void;
  toBase64: (file: File) => Promise<string>;
  resetState: () => void;
}

const VALID_FILE_TYPES = [
  'application/pdf',
  'application/msword',
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
];

export function useResumeUpload(): UseResumeUploadReturn {
  const selectedFiles = ref<File[]>([]);
  const selectedOption = ref<EngagementMode>(EngagementMode.SCREEN);

  function addFiles(files: File[]) {
    const validFiles = files.filter((file) => VALID_FILE_TYPES.includes(file.type));
    selectedFiles.value.push(...validFiles);
  }

  function removeFile(index: number) {
    selectedFiles.value.splice(index, 1);
  }

  function clearAllFiles() {
    selectedFiles.value = [];
  }

  function toBase64(file: File): Promise<string> {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.readAsDataURL(file);
      reader.onload = () => resolve(`${reader.result}`.split('base64,')[1] ?? '');
      reader.onerror = reject;
    });
  }

  function resetState() {
    selectedFiles.value = [];
    selectedOption.value = EngagementMode.SCREEN;
  }

  return {
    selectedFiles,
    selectedOption,
    addFiles,
    removeFile,
    clearAllFiles,
    toBase64,
    resetState,
  };
}
